/**
 * One reading of a server answer for every chart, list and page (Authorization
 * part 8a, item 6; plan §15). The servers decide; the UI only tells apart:
 *
 * - `ok`        the answer carries data;
 * - `no_data`   the answer is fine and empty (nothing in the scope / filters);
 * - `no_access` refused for a permission (403 `permission_denied` and the
 *               other 403 reasons, the legacy `permission_denied` message);
 * - `not_found` the record does not exist OR lies outside the active scope
 *               (404 `not_found`: the server gives the same answer for both);
 * - `error`     anything else that failed.
 *
 * The gateway helpers resolve refusals as `{success: false, ...}` bodies, so
 * the body is what is read (reason first, then the legacy message / code).
 */
export type AccessAnswer = 'ok' | 'no_access' | 'not_found' | 'error';
export type DataState = AccessAnswer | 'no_data';

const NO_ACCESS_REASONS = new Set([
  'permission_denied',
  'application_not_allowed',
  'internal_only',
  'forbidden',
]);

const LEGACY_DENIED_MESSAGES = new Set([
  'permission_denied',
  'You do not have permission to perform this action.',
]);

const numberOf = (value: unknown): number | null => {
  const n = Number(value);
  return value !== null && value !== undefined && value !== '' && Number.isFinite(n) ? n : null;
};

/** The HTTP status an answer body carries (`code`, `status_code` or `status`), or null. */
export function statusOfAnswer(response: unknown): number | null {
  if (!response || typeof response !== 'object') return null;
  const body = response as Record<string, unknown>;
  return numberOf(body.code) ?? numberOf(body.status_code) ?? numberOf(body.status);
}

/** How the server answered: data, a permission refusal, "not found", or an error. */
export function accessAnswer(response: unknown): AccessAnswer {
  if (typeof response === 'string') {
    return LEGACY_DENIED_MESSAGES.has(response) ? 'no_access' : 'error';
  }
  if (!response || typeof response !== 'object') return 'error';
  const body = response as Record<string, unknown>;
  if (body.success === true) return 'ok';

  const reason = typeof body.reason === 'string' ? body.reason : null;
  const status = statusOfAnswer(body);
  if (reason === 'not_found' || status === 404) return 'not_found';
  if (
    (reason !== null && NO_ACCESS_REASONS.has(reason)) ||
    status === 403 ||
    (typeof body.message === 'string' && LEGACY_DENIED_MESSAGES.has(body.message))
  ) {
    return 'no_access';
  }
  return 'error';
}

/**
 * The list an answer carries under `key`, or [] when it carries none.
 *
 * A filter that lies wholly outside the active scope (a header country the
 * role's contracts are not in) is answered with "no rows" as an empty
 * `data: []`, not in the endpoint's own shape (`{ results: [] }`,
 * `{ graphData: [] }`, ...). A chart that reads its rows through this shows
 * its "no data" state for that answer.
 */
export function listOf<T = any>(data: unknown, key: string): T[] {
  const rows =
    data && typeof data === 'object' ? (data as Record<string, unknown>)[key] : undefined;
  return Array.isArray(rows) ? (rows as T[]) : [];
}

/**
 * `data` when it is an object in the endpoint's own shape, else `empty`: the
 * "no rows" answer of a filter outside the scope (`data: []`, see listOf)
 * read as the endpoint's own empty answer.
 */
export function orEmpty<T extends object>(data: unknown, empty: T): T {
  return data && typeof data === 'object' && !Array.isArray(data) ? (data as T) : empty;
}

/** null, undefined, an empty list or an object without keys. */
export const isEmptyData = (data: unknown): boolean => {
  if (data === null || data === undefined) return true;
  if (Array.isArray(data)) return data.length === 0;
  if (typeof data === 'object') return Object.keys(data as object).length === 0;
  return false;
};

/**
 * The state of a chart / list after its request: `accessAnswer`, plus
 * `no_data` for a fine but empty answer. `isEmpty` decides emptiness on
 * `response.data` (default: null / empty list / empty object).
 */
export function dataState(
  response: unknown,
  // eslint-disable-next-line no-unused-vars
  isEmpty: (data: unknown) => boolean = isEmptyData,
): DataState {
  const answer = accessAnswer(response);
  if (answer !== 'ok') return answer;
  return isEmpty((response as { data?: unknown }).data) ? 'no_data' : 'ok';
}
